import { flatten, groupBy } from 'lodash';
import moment from 'moment';
import exportExcel from '@ubeya/shared/utils/exportExcel';
import { getEmployeeDisplayName } from '@ubeya/shared-web/utils/employee';
import { getDurationFromMilliseconds, getMillisecondsFromDecimal } from '@ubeya/shared/utils/time';
import {
  API_DATE_TIME_FORMAT,
  API_DATE_FORMAT,
  TIMESHEET_GROUP_BY_OPTIONS_ENUM,
  ORDER_STATUSES,
  ACCOUNT_BILLING_STATUS_MAP
} from '@ubeya/shared/constants';
import { mappedArray } from '@ubeya/shared/utils/array';
import { keyReportMethodsByClockType } from '@ubeya/shared/utils/reportMethod';
import {
  SORT_BY_NAME,
  SORT_BY_ACTUAL_TOTAL,
  SORT_BY_DATE,
  SORT_BY_NUMBER,
  SORT_BY_NUMBER_NO_KEY,
  SORT_BY_DATE_AND_TIME,
  SORT_BY_TOTAL,
  SORT_BY_TIME,
  SORT_BY_SUPPLIER_NAME,
  SORT_BY_BREAKS_DURATION,
  SORT_BY_STRING,
  SORT_BY_ONLY_EXTERNAL_POSITION,
  SORT_BY_BOOLEAN,
  SORT_BY_POSITION
} from '@ubeya/shared/utils/sorting';
import { getTimesheetFieldsForExport } from '@ubeya/shared/utils/timesheetFields';
import { PAYROLL_PROCESS_STATUS } from '@ubeya/shared-web/constants';
import { generateShiftName } from '@ubeya/shared/utils/scheduling';
import { DENVER_PAYROLL_LEGAL_NAME_FIELD_ID } from '@ubeya/shared/utils/accounts';
import { conditionalColumns } from './csvColumns';
import { reduceEmployeeFields } from './fields';
import { isProjectMultiDay } from 'scheduling/utils';
import { DAILY_EMPLOYEES_SLUG } from 'timeSheet/constants';
import { applyFiltersOnTimesheetData } from 'timeSheet/utils';

const generateSortByNameValues = ({ sortBy, item }) =>
  sortBy === 'name'
    ? item.employee
    : sortBy === 'firstName'
    ? { firstName: item.employee.firstName }
    : sortBy === 'lastName'
    ? { lastName: item.employee.lastName }
    : {};

const generateProjectName = ({ project, dateFormat, mappedLocations, mappedLabels }) => {
  if (!project || !project.projectId) {
    if (project?.employee?.isDaily) return DAILY_EMPLOYEES_SLUG;
    return '';
  }
  const { projectId, name, labelId, locationId, projectStartDate, projectEndDate } = project;

  const isMultiDay = isProjectMultiDay({ date: projectStartDate, endDate: projectEndDate });
  const nameValue = name || (labelId ? mappedLabels?.[labelId]?.title : mappedLocations?.[locationId]?.name);

  return `${moment(projectStartDate).format(dateFormat)}${
    isMultiDay ? ` - ${moment(projectEndDate).format(dateFormat)}` : ''
  } | ${nameValue || ''}.${projectId}`;
};

const generateMapGroupValueLatLng = (value, mappedLocations) => {
  const location = mappedLocations?.[value?.locationId];
  if (!location) {
    return null;
  }

  const { lat, lng } = location;
  if (!lat || !lng) {
    return null;
  }

  //separate via '|' since '-' can be actual minus
  return `${lat}|${lng}`;
};

export const MAP_GROUP = {
  date: (value) => moment(value?.date, API_DATE_FORMAT).toISOString(),
  project: (value, mappedLocations, mappedLabels, dateFormat) =>
    generateProjectName({ project: value, dateFormat, mappedLocations, mappedLabels }),
  mainEvent: (value) => value?.mainProjectId || null,
  position: (value) => value?.positionId || null,
  employee: (value) => value?.employee?.id,
  poNumber: (value) => value?.poNumber || null,
  shift: (value) => value?.shiftId,
  startTime: (value) => moment(value?.scheduleIn, API_DATE_TIME_FORMAT).toISOString(),
  endTime: (value) => moment(value?.scheduleOut, API_DATE_TIME_FORMAT).toISOString(),
  location: (value, mappedLocations) => (mappedLocations?.[value?.locationId] ? value?.locationId : null),
  costCenter: (value) => value?.costCenter?.name || null,
  client: (value) => value?.clientId || null,
  latLng: generateMapGroupValueLatLng,
  noGroupBy: () => 'all'
};

const SORTER_MAP = {
  SORT_BY_PAYROLL_TOTAL: SORT_BY_NUMBER('payrollTotal'),
  SORT_BY_GROUP_VALUE: SORT_BY_DATE('groupValue'),
  SORT_BY_GROUP_VALUE_STRING: SORT_BY_STRING('groupValue'),
  SORT_BY_DATE: SORT_BY_DATE('date'),
  SORT_BY_BREAKS: SORT_BY_BREAKS_DURATION('breaks')
};

const TABLE_SORT_MAP = {
  name: SORT_BY_NAME,
  isLocked: SORT_BY_BOOLEAN('isLocked'),
  in: SORT_BY_DATE_AND_TIME('actualIn'),
  out: SORT_BY_DATE_AND_TIME('actualOut'),
  checkIn: SORT_BY_DATE_AND_TIME('actualCheckIn'),
  checkOut: SORT_BY_DATE_AND_TIME('actualCheckOut'),
  date: SORT_BY_TIME('scheduleIn'),
  total: SORT_BY_TOTAL,
  totalWithoutBreaks: SORT_BY_NUMBER('actualTotalWithoutBreaks'),
  clientWageTotal: SORT_BY_NUMBER('clientWageTotal'),
  rating: SORT_BY_NUMBER('rating'),
  totalBreaktimesDuration: SORT_BY_NUMBER('totalBreaktimesDuration'),
  breaks: SORT_BY_BREAKS_DURATION('breaktimes'),
  clientWage: SORT_BY_NUMBER('clientWage.hourlyWage')
};

const includesTerm = (str = '', search = '') => str.toLowerCase().includes(search.toLowerCase());

export const getPayrollLegalName = (employee) =>
  (employee?.fields || [])
    .find(({ id }) => id === DENVER_PAYROLL_LEGAL_NAME_FIELD_ID)
    ?.value?.toString()
    .trim() || '';

const sortedGroupedData = ({ sortingColumns, nameColumnDateSort, groupLists, groupByKey, sort }) => {
  if (groupByKey === 'noGroupBy' && groupLists.length > 0) {
    groupLists.forEach(({ subRows }) =>
      subRows.sort((a, b) =>
        sort.sortDirection === 'asc' ? SORTER_MAP.SORT_BY_DATE(a, b) : SORTER_MAP.SORT_BY_DATE(b, a)
      )
    );
    return groupLists;
  }
  if (groupByKey === 'date') {
    groupLists.sort(SORTER_MAP.SORT_BY_GROUP_VALUE);
  }
  if (groupByKey === 'project' && sort.sortBy === 'name') {
    groupLists.sort((a, b) => {
      const diff = SORTER_MAP.SORT_BY_DATE(a, b);
      if (diff === 0) {
        return b.isMultiDay ? 1 : a.isMultiDay ? -1 : 0;
      }
      return diff;
    });
  }

  if (!nameColumnDateSort && groupByKey === 'employee') {
    // The only case of sorting drawers: sorting by name (or firstName / lastName) + sorting by total time (and not sub rows) + sorting by total salary + sorting by breaks duration
    if (['name', 'firstName', 'lastName'].includes(sort.sortBy)) {
      // side effect for task DEV-965 to sort also dates for employee card
      // consider in future to add additinal flag for dates sorting
      groupLists.forEach(({ subRows }) =>
        subRows.sort((a, b) =>
          sort.sortDirection === 'asc' ? SORTER_MAP.SORT_BY_DATE(a, b) : SORTER_MAP.SORT_BY_DATE(b, a)
        )
      );

      return groupLists.sort(({ subRows: a }, { subRows: b }) => {
        const aValue = generateSortByNameValues({ sortBy: sort.sortBy, item: a[0] });
        const bValue = generateSortByNameValues({ sortBy: sort.sortBy, item: b[0] });

        return sort.sortDirection === 'asc' ? TABLE_SORT_MAP.name(aValue, bValue) : TABLE_SORT_MAP.name(bValue, aValue);
      });
    }

    if (sort.sortBy === 'breaks') {
      groupLists.forEach(({ subRows }) => subRows.sort(SORTER_MAP.SORT_BY_BREAKS));

      return groupLists.sort((a, b) =>
        sort.sortDirection === 'asc'
          ? TABLE_SORT_MAP.totalBreaktimesDuration(a, b)
          : TABLE_SORT_MAP.totalBreaktimesDuration(b, a)
      );
    }

    if (sort.sortBy === 'total') {
      // sort summarized rows
      groupLists.sort((a, b) =>
        sort.sortDirection === 'asc' ? SORT_BY_ACTUAL_TOTAL(a, b) : SORT_BY_ACTUAL_TOTAL(b, a)
      );

      // sort subRows
      groupLists.forEach(({ subRows }) =>
        subRows.sort((a, b) => (sort.sortDirection === 'asc' ? SORT_BY_ACTUAL_TOTAL(a, b) : SORT_BY_ACTUAL_TOTAL(b, a)))
      );

      return groupLists;
    }

    if (sort.sortBy === 'totalSalary') {
      groupLists.sort((a, b) =>
        sort.sortDirection === 'asc' ? SORTER_MAP.SORT_BY_PAYROLL_TOTAL(a, b) : SORTER_MAP.SORT_BY_PAYROLL_TOTAL(b, a)
      );
      groupLists.forEach(({ subRows }) =>
        subRows.sort((a, b) =>
          sort.sortDirection === 'asc'
            ? SORT_BY_NUMBER_NO_KEY(a?.payroll?.value || 0, b?.payroll?.value || 0)
            : SORT_BY_NUMBER_NO_KEY(b?.payroll?.value || 0, a?.payroll?.value || 0)
        )
      );

      return groupLists;
    }
  }

  if (sort.sortBy === 'shift') {
    const quantitySorter = SORT_BY_NUMBER('shift');

    // sort summarized rows - by shift quantity
    groupLists.sort((a, b) => (sort.sortDirection === 'asc' ? quantitySorter(a, b) : quantitySorter(b, a)));
  }

  groupLists.forEach(({ subRows }) =>
    subRows.sort((a, b) =>
      sort.sortDirection === 'asc' ? sortingColumns[sort.sortBy]?.(a, b) : sortingColumns[sort.sortBy]?.(b, a)
    )
  );
  return groupLists;
};

export const filterData = (data, search) =>
  !search
    ? data
    : data.filter(
        ({ employee, actualInReporter, invoiceNumber, actualOutReporter, projectName }) =>
          includesTerm(employee?.firstName, search) ||
          includesTerm(employee?.lastName, search) ||
          includesTerm(`${employee?.firstName} ${employee?.lastName}`, search) ||
          includesTerm(employee?.preferredName, search) ||
          includesTerm(getPayrollLegalName(employee), search) ||
          includesTerm(employee?.position, search) ||
          includesTerm(employee?.identification, search) ||
          includesTerm(actualInReporter?.name, search) ||
          includesTerm(actualOutReporter?.name, search) ||
          includesTerm(actualInReporter?.location?.address, search) ||
          includesTerm(actualOutReporter?.location?.address, search) ||
          includesTerm(invoiceNumber || '', search) ||
          includesTerm(projectName || '', search)
      );

export const formatProjectDataSlots = (data, employeesMap, byClient) => {
  return data.map((item) => {
    const relevantStartTime = byClient ? item?.timesheet?.clientStartTime : item?.timesheet?.startTime;
    const relevantEndTime = byClient ? item?.timesheet?.clientEndTime : item?.timesheet?.endTime;

    return {
      id: item?.id,
      isLocked: !!item?.isLocked,
      employee: employeesMap[item.employeeId],
      positionId: item?.positionId,
      date: item?.date,
      shiftDate: item?.shiftDate,
      isOverbooking: item?.isOverbooking,
      endDate: item?.endDate,
      invoiceNumber: item?.invoiceNumber,
      timesheetStartTime: item?.timesheet?.startTime,
      timesheetEndTime: item?.timesheet?.endTime,
      isExternal: item?.isExternal,
      supplierData: item?.isExternal
        ? {
            id: item?.supplierAccountId,
            name: item?.supplierAccountName,
            branchId: item?.supplierBranchId,
            logo: item?.supplierAccountLogo,
            positionSlug: item?.supplierPositionSlug
          }
        : undefined,
      originSupplierData:
        item?.isExternal && item?.originSupplierAccountName
          ? {
              name: item?.originSupplierAccountName,
              logo: item?.originSupplierAccountLogo
            }
          : undefined,
      isThirdParty: item?.isThirdParty,
      actualIn: relevantStartTime,
      actualOut: relevantEndTime,
      paidHours: item?.paidHours,
      actualCheckIn: item?.timesheet?.checkInTime,
      actualCheckOut: item?.timesheet?.checkOutTime,
      actualInReporter: {
        type: item?.timesheet?.startTimesheetReport?.adminId
          ? 'admin'
          : item?.timesheet?.startTimesheetReport?.userId
          ? 'location'
          : null,
        lat: item?.timesheet?.startTimesheetReport?.lat,
        lng: item?.timesheet?.startTimesheetReport?.lng
      },
      actualOutReporter: {
        type: item?.timesheet?.endTimesheetReport?.adminId
          ? 'admin'
          : item?.timesheet?.endTimesheetReport?.userId
          ? 'location'
          : null,
        lat: item?.timesheet?.endTimesheetReport?.lat,
        lng: item?.timesheet?.endTimesheetReport?.lng
      },
      actualCheckInReporter: {
        type: item?.timesheet?.checkInTimesheetReport?.adminId
          ? 'admin'
          : item?.timesheet?.checkInTimesheetReport?.userId
          ? 'location'
          : null,
        lat: item?.timesheet?.checkInTimesheetReport?.lat,
        lng: item?.timesheet?.checkInTimesheetReport?.lng
      },
      actualCheckOutReporter: {
        type: item?.timesheet?.checkOutTimesheetReport?.adminId
          ? 'admin'
          : item?.timesheet?.checkOutTimesheetReport?.userId
          ? 'location'
          : null,
        lat: item?.timesheet?.checkOutTimesheetReport?.lat,
        lng: item?.timesheet?.checkOutTimesheetReport?.lng
      },
      reportMethods: keyReportMethodsByClockType(item?.timesheet?.reportMethods),
      scheduleIn: item?.startTime,
      scheduleOut: item?.endTime,
      approved: item?.timesheet?.approved || false,
      payrollProcess: item?.payrollProcess,
      adminApproved: item?.timesheet?.adminApproved || null,
      clientApproved: item?.timesheet?.clientApproved || null,
      clientDeclined: item?.timesheet?.clientDeclined || null,
      breaktimes: item?.timesheet?.breaktimes,
      timesheetId: item?.timesheet?.id,
      labelId: item?.labelId,
      bookerId: item?.bookerId,
      branchId: item?.branchId,
      clientBranchId: item?.clientBranchId,
      projectId: item?.projectId,
      shiftId: item?.shiftId,
      name: item?.name,
      mainProjectId: item?.mainProjectId || null,
      mainProjectName: item?.mainProjectName || null,
      mainProjectIdentifier: item?.mainProjectIdentifier || null,
      mainProjectGlCode: item?.mainProjectGlCode || null,
      mainProjectDoorTime: item?.mainProjectDoorTime || null,
      mainProjectEstimatedAttendance: item?.mainProjectEstimatedAttendance ?? null,
      mainProjectExpectedRevenue: item?.mainProjectExpectedRevenue ?? null,
      mainProjectTotalActualRevenue: item?.mainProjectTotalActualRevenue ?? null,
      mainProjectActualAttendance: item?.mainProjectActualAttendance ?? null,
      locationId: item?.locationId,
      comments: item?.comments,
      rating: item?.employeeSlot?.rating,
      review: item?.employeeSlot?.review,
      captainType: item?.employeeSlot?.captainType,
      wage: item?.wage,
      clientWage: item?.clientWage,
      employeeSlotId: item?.employeeSlot?.id,
      warnings: item?.warnings,
      actualTotal: totalTime(relevantStartTime, relevantEndTime),
      scheduleTotal: totalTime(item?.startTime, item?.endTime),
      difference:
        (relevantStartTime &&
          relevantEndTime &&
          item?.startTime &&
          item?.endTime &&
          totalTime(relevantStartTime, relevantEndTime) - totalTime(item?.startTime, item?.endTime)) ||
        0
    };
  });
};

export const formatShift = ({
  item,
  employeesMap,
  isJob = false,
  isPto = false,
  isLeave = false,
  deductBreaktimesIsActive = false,
  byClient = false,
  mappedClientBranches,
  mappedLabels,
  mappedLocations,
  dateFormat,
  billingStatus,
  mappedSuppliers
}) => {
  const relevantStartTime =
    byClient || item?.isExternal
      ? item?.timesheet?.clientStartTime || item?.timesheet?.startTime
      : item?.timesheet?.startTime;

  const relevantEndTime =
    byClient || item?.isExternal
      ? item?.timesheet?.clientEndTime || item?.timesheet?.endTime
      : item?.timesheet?.endTime;

  const totalBreakTime = (item?.timesheet?.breaktimes || []).reduce(
    (sum, { startTime, endTime }) => sum + totalTime(startTime, endTime),
    0
  );

  const shiftTotalTime = totalTime(item?.startTime, item?.endTime);

  const actualTotalTimeWithoutBreaks = totalTime(relevantStartTime, relevantEndTime);

  const amIFreemium = billingStatus === ACCOUNT_BILLING_STATUS_MAP.CLIENT_FREEMIUM;

  // A freemium client's external rows read the same total as the order table (groupByOSProject): breaks come
  // off per the accounts_clients flag of the supplier that staffed the row, not per my own payroll config.
  const deductClientBreaktimesIsActive = mappedSuppliers?.[item?.supplierAccountId]?.deductClientBreaktimesIsActive;

  const shouldDeductBreaktimes =
    amIFreemium && item?.isExternal ? deductClientBreaktimesIsActive : deductBreaktimesIsActive;

  const actualTotalTime = actualTotalTimeWithoutBreaks - (shouldDeductBreaktimes ? totalBreakTime : 0);

  const differenceInTime = actualTotalTime - shiftTotalTime || 0;

  const mappedTimesheetFieldsValues = (item?.timesheet?.timesheetFields || []).reduce((output, current) => {
    output[current.fieldId] = current.value;
    return output;
  }, {});

  return {
    id: item?.id,
    clientShiftId: item?.clientShiftId,
    payrollProcess: item?.payrollProcess,
    isLocked: item?.isLocked,
    // Leave-only employees are absent from employeesMap; fall back to embedded `employee`, then slot.
    employee: employeesMap?.[item?.employeeId] ||
      item?.employee || { ...item?.employeeSlot, id: item?.employeeSlot?.employeeId },
    positionId: item?.positionId,
    date: item?.date,
    shiftDate: item?.shiftDate,
    endDate: item?.endDate,
    additionalRevenues: item?.additionalRevenues,
    actualIn: relevantStartTime,
    actualOut: relevantEndTime,
    paidHours: item?.paidHours,
    clientActualIn: item?.timesheet?.clientStartTime,
    clientActualOut: item?.timesheet?.clientEndTime,
    actualCheckIn: item?.timesheet?.checkInTime,
    actualCheckOut: item?.timesheet?.checkOutTime,
    timesheetStartTime: item?.timesheet?.startTime,
    timesheetEndTime: item?.timesheet?.endTime,
    userActualIn: item?.timesheet?.userStartTime,
    userActualOut: item?.timesheet?.userEndTime,
    userActualCheckIn: item?.timesheet?.userCheckInTime,
    userActualCheckOut: item?.timesheet?.userCheckOutTime,
    isSystem: item?.timesheet?.isSystem,
    clientShift: item?.clientShift,
    isExternal: item?.isExternal,
    needed: item?.needed,
    supplierData: item?.isExternal
      ? {
          id: item?.supplierAccountId,
          name: item?.supplierAccountName,
          branchId: item?.supplierBranchId,
          logo: item?.supplierAccountLogo,
          positionSlug: item?.supplierPositionSlug
        }
      : undefined,
    originSupplierData:
      item?.isExternal && item?.originSupplierAccountName
        ? {
            name: item?.originSupplierAccountName,
            logo: item?.originSupplierAccountLogo,
            timesheetConfig: item?.originSupplierTimesheetConfig
          }
        : undefined,
    isThirdParty: item?.isThirdParty,
    actualInReporter: {
      type: item?.timesheet?.startTimesheetReport?.adminId
        ? 'admin'
        : item?.timesheet?.startTimesheetReport?.userId
        ? 'location'
        : null,
      lat: item?.timesheet?.startTimesheetReport?.lat,
      lng: item?.timesheet?.startTimesheetReport?.lng
    },
    actualOutReporter: {
      type: item?.timesheet?.endTimesheetReport?.adminId
        ? 'admin'
        : item?.timesheet?.endTimesheetReport?.userId
        ? 'location'
        : null,
      lat: item?.timesheet?.endTimesheetReport?.lat,
      lng: item?.timesheet?.endTimesheetReport?.lng
    },
    actualCheckInReporter: {
      type: item?.timesheet?.checkInTimesheetReport?.adminId
        ? 'admin'
        : item?.timesheet?.checkInTimesheetReport?.userId
        ? 'location'
        : null,
      lat: item?.timesheet?.checkInTimesheetReport?.lat,
      lng: item?.timesheet?.checkInTimesheetReport?.lng
    },
    actualCheckOutReporter: {
      type: item?.timesheet?.checkOutTimesheetReport?.adminId
        ? 'admin'
        : item?.timesheet?.checkOutTimesheetReport?.userId
        ? 'location'
        : null,
      lat: item?.timesheet?.checkOutTimesheetReport?.lat,
      lng: item?.timesheet?.checkOutTimesheetReport?.lng
    },
    reportMethods: keyReportMethodsByClockType(item?.timesheet?.reportMethods),
    tags: item?.tags || [],
    scheduleIn: item?.startTime,
    hasChangedAfterApprove: item?.timesheet?.hasChangedAfterApprove,
    scheduleOut: item?.endTime,
    approved: item?.timesheet?.approved || false,
    adminApproved: item?.timesheet?.adminApproved || null,
    clientApproved: item?.timesheet?.clientApproved || null,
    clientDeclined: item?.timesheet?.clientDeclined || null,
    employeeApproved: item?.timesheet?.employeeApproved || null,
    employeeDeclined: item?.timesheet?.employeeDeclined || null,
    costCenter: item?.costCenter || null,
    clientCostCenter: item?.clientCostCenter || null,
    breaktimes: item?.timesheet?.breaktimes,
    timesheetId: item?.timesheet?.id,
    labelId: item?.labelId,
    bookerId: item?.bookerId,
    branchId: item?.branchId,
    clientBranchId: item?.clientBranchId,
    clientBranch: mappedClientBranches?.[item.clientBranchId],
    clientId: item?.clientId,
    clientBooker: item.clientBooker || null,
    invoiceNumber: item?.invoiceNumber,
    poNumber: item?.poNumber,
    projectId: item?.projectId,
    projectIsArchived: item?.projectIsArchived,
    projectIsInvoiceIssued: item?.projectIsInvoiceIssued,
    projectIsPayrollLock: item?.projectIsPayrollLock,
    projectStartDate: item?.projectStartDate,
    projectEndDate: item?.projectEndDate,
    projectName: generateProjectName({ project: item, dateFormat, mappedLocations, mappedLabels }),
    projectLocationId: item?.projectLocationId,
    projectComments: item?.projectComments,
    projectPublicComments: item?.projectPublicComments,
    payrollNotes: item?.payrollNotes || item?.employeeSlot?.payrollNotes,
    schedulingNotes: item?.notes || item?.employeeSlot?.notes,
    shiftId: item?.shiftId,
    name: item?.name,
    mainProjectId: item?.mainProjectId || null,
    mainProjectName: item?.mainProjectName || null,
    mainProjectIdentifier: item?.mainProjectIdentifier || null,
    mainProjectGlCode: item?.mainProjectGlCode || null,
    mainProjectDoorTime: item?.mainProjectDoorTime || null,
    mainProjectEstimatedAttendance: item?.mainProjectEstimatedAttendance ?? null,
    mainProjectExpectedRevenue: item?.mainProjectExpectedRevenue ?? null,
    mainProjectTotalActualRevenue: item?.mainProjectTotalActualRevenue ?? null,
    mainProjectActualAttendance: item?.mainProjectActualAttendance ?? null,
    locationId: item?.locationId,
    comments: item?.comments,
    rating: item?.rating || item?.employeeSlot?.rating,
    review: item?.review || item?.employeeSlot?.review,
    captainType: item?.captainType || item?.employeeSlot?.captainType,
    clientName: item?.client?.name,
    dummy: item?.dummy ?? false,
    jobTypeId: item?.jobTypeId || 1, // default 'in office' for placeholders
    isJob,
    isPto,
    isLeave,
    ptoType: isPto ? item?.type : null,
    leaveType: isLeave ? item?.leaveType : null,
    leaveSupportsManualRate: isLeave && !!item?.supportsManualRate,
    wage: item?.wage,
    clientWage: item?.clientWage,
    employeeSlotId: item?.employeeSlotId,
    invoiceStatus: item?.invoiceStatus !== undefined ? item?.invoiceStatus : item?.employeeSlot?.invoiceStatus,
    warnings: item?.warnings,
    actualTotalWithoutBreaks: actualTotalTimeWithoutBreaks,
    actualTotal: actualTotalTime,
    actualBreaksTotal: totalBreakTime || 0,
    scheduleTotal: shiftTotalTime,
    difference: differenceInTime,
    timesheetFields: item?.timesheet?.timesheetFields || [],
    mappedTimesheetFieldsValues,
    projectFields: item?.projectFields || []
  };
};

export const formatShifts = ({
  data,
  employeesMap,
  deductBreaktimesIsActive,
  mappedClientBranches,
  mappedLabels,
  mappedLocations,
  dateFormat,
  byClient = false,
  isJob = false,
  isPto = false,
  isLeave = false,
  billingStatus,
  mappedSuppliers
}) =>
  data.map((item) =>
    formatShift({
      item,
      employeesMap,
      isJob,
      isPto,
      isLeave,
      deductBreaktimesIsActive,
      byClient,
      mappedClientBranches,
      mappedLabels,
      mappedLocations,
      dateFormat,
      billingStatus,
      mappedSuppliers
    })
  );

// Adapt the flat leave-record row from the payroll response into formatShift's expected shape.
export const formatLeaveRecords = ({
  data,
  employeesMap,
  mappedClientBranches,
  mappedLabels,
  mappedLocations,
  dateFormat
}) =>
  data.map((leaveRecord) => {
    // Spread first so extras (payrollProcess, wage, tags, …) reach the grid; normalize timesheet shape below.
    const beTimesheet = leaveRecord.timesheet || {};
    const adapted = {
      ...leaveRecord,
      id: leaveRecord.id,
      employeeId: leaveRecord.employeeId,
      branchId: leaveRecord.branchId,
      date: leaveRecord.date,
      startTime: leaveRecord.startTime,
      endTime: leaveRecord.endTime,
      leaveType: leaveRecord.leaveType,
      wage: leaveRecord.wageSource === 'manual' ? { ...leaveRecord.wage, type: 'manual' } : leaveRecord.wage,
      timesheet: {
        id: beTimesheet.id ?? leaveRecord.timesheetId,
        startTime: beTimesheet.startTime ?? leaveRecord.startTime,
        endTime: beTimesheet.endTime ?? leaveRecord.endTime,
        approved: beTimesheet.approved === true,
        adminApproved: beTimesheet.adminApproved ?? null
      }
    };

    return formatShift({
      item: adapted,
      employeesMap,
      isLeave: true,
      byClient: false,
      mappedClientBranches,
      mappedLabels,
      mappedLocations,
      dateFormat
    });
  });

export const formatDummyShifts = (data, employeesMap) => {
  return data.map((item) => ({
    date: item?.date,
    employee: employeesMap[item.employeeId],
    scheduleTotal: 0,
    jobTypeId: 1, // default 'in office' for placeholders
    isJob: true,
    dummy: true,
    approved: item?.timesheet?.approved || false,
    branchId: item?.branchId
  }));
};

const calculateTotalHoursInDecimal = (start, end) => {
  // Parse the input strings into Date objects
  const clockInTime = new Date(start);
  const clockOutTime = new Date(end);

  // Calculate the difference in milliseconds
  const timeDifference = clockOutTime - clockInTime;

  // Convert milliseconds to hours (1 hour = 3600 seconds * 1000 milliseconds)
  const hoursWorked = timeDifference / (1000 * 60 * 60);

  return hoursWorked.toFixed(2); // Return the result with two decimal places
};

/* payroll result returns decimal values, which we aggregate in order to calculate the total.
   in flow of timesheet, we calculate just the difference,
   so in order to keep the results same, we calculate here first in decimal (like backend returns in payroll case),
   and then convert to milliseconds */

export const totalTime = (start, end) => {
  if (!start || !end) {
    return 0;
  }

  const inDecimal = calculateTotalHoursInDecimal(start, end);

  return getMillisecondsFromDecimal(inDecimal);
};

export const getEmployeesMap = (employees) => mappedArray(employees, ({ id }) => id);

const createDummyShifts = (start, end, jobs, employees, employeesMap, interval = 'days', selectedBranches = []) => {
  const minValue = moment(start);
  const maxValue = moment(end);

  const days = maxValue.diff(minValue, interval) + 1;
  const startDate = minValue.subtract(1, interval);

  const filteredDailyEmployees = employees
    .filter(({ isDeleted, isActive, isDaily }) => !isDeleted && !!isActive && !!isDaily)
    .flatMap((employee) => {
      const { id, workDays = [] } = employee;

      // For each workDays entry, check if its branch is in selectedBranches (or if none selected, allow all)
      return workDays
        .filter(({ branchId }) => selectedBranches.length === 0 || selectedBranches.includes(branchId))
        .map(({ branchId, days: currentDays }) => ({
          id,
          workDays: currentDays,
          branchId
        }));
    });

  const dummyShifts = [];

  const employeeBranchJobsByDate = (jobs || []).reduce((all, { date, employee, branchId }) => {
    if (!all[date]) {
      all[date] = new Set();
    }
    all[date].add(`${employee?.id}_${branchId}`);
    return all;
  }, {});

  [...Array(days)].forEach(() => {
    const day = startDate.add(1, interval);

    const formattedDate = day.format(API_DATE_FORMAT);
    const employeeBranchJobs = employeeBranchJobsByDate[formattedDate] || new Set();
    const dayAccordingToWorkingDays = day.get('day') + 1;

    const dailyEmployees = filteredDailyEmployees
      .filter(({ id, branchId }) => !employeeBranchJobs.has(`${id}_${branchId}`))
      .filter(({ workDays }) => workDays?.some?.((workingDay) => dayAccordingToWorkingDays === workingDay));

    dailyEmployees.forEach(({ id, branchId }) => {
      dummyShifts.push({
        employeeId: id,
        date: moment(day).format(API_DATE_FORMAT),
        dummy: true,
        branchId
      });
    });
  });

  const result = formatDummyShifts(dummyShifts, employeesMap, true);

  return result;
};

export const groupByOSProject = ({
  project,
  sort,
  mapPayroll,
  mappedSuppliers,
  mappedAdmins,
  mappedSupplierPositions,
  filterDraftOrders = true,
  t
}) => {
  const supplierNameSorter = (a, b) => SORT_BY_SUPPLIER_NAME(a, b, mappedSuppliers);
  const positionSorter = (a, b) => SORT_BY_ONLY_EXTERNAL_POSITION(a, b, mappedSupplierPositions, t);
  const clientPositionSorter = (a, b) => SORT_BY_POSITION(a, b, mappedSupplierPositions, t);

  const sorter =
    sort.sortBy === 'supplierAccountId'
      ? supplierNameSorter
      : sort.sortBy === 'positionId'
      ? positionSorter
      : sort.sortBy === 'clientPositionId'
      ? clientPositionSorter
      : TABLE_SORT_MAP[sort.sortBy];

  const mappedProjectShifts = mappedArray(project?.shifts || [], (item) => item.id);

  const formattedData = flatten(
    (project?.orders || [])
      /* Hide orders with draft */
      .filter(({ status }) => (filterDraftOrders ? status !== ORDER_STATUSES.DRAFT.label : true))
      .map(({ shifts, clientBranchId, supplierAccountId, supplierBranchId, id: orderId }) =>
        shifts.map((shift) => ({ ...shift, supplierAccountId, clientBranchId, supplierBranchId, orderId }))
      )
  )
    .reduce((output, elem) => {
      const {
        slots,
        orderId,
        supplierAccountId,
        supplierBranchId,
        clientBranchId,
        locationId,
        positionId,
        startTime,
        id: shiftId,
        date,
        endTime,
        comments,
        originSupplierAccountsData,
        isThirdParty,
        mappedToAnyShiftInCurrentProject,
        branchId: originSupplierBranchId
      } = elem;

      const clientPositionId =
        mappedToAnyShiftInCurrentProject &&
        mappedToAnyShiftInCurrentProject?.[0] &&
        mappedProjectShifts?.[mappedToAnyShiftInCurrentProject?.[0]]?.positionId;
      const clientLocationId =
        mappedToAnyShiftInCurrentProject &&
        mappedToAnyShiftInCurrentProject?.[0] &&
        mappedProjectShifts?.[mappedToAnyShiftInCurrentProject?.[0]]?.locationId;
      return [
        ...output,
        ...slots
          .filter(({ employeeSlot }) => !!employeeSlot)
          .map(({ id: slotId, employeeSlot, timesheet, clientWage, isLocked }) => {
            const payroll = timesheet?.id && mapPayroll ? mapPayroll?.[timesheet.id] : undefined;
            const deductClientBreaktimesIsActive = mappedSuppliers?.[supplierAccountId]?.deductClientBreaktimesIsActive;

            const clientTotalTime = totalTime(timesheet?.clientStartTime, timesheet?.clientEndTime);

            const clientBreaksTotalTime = (timesheet?.breaktimes || []).reduce(
              (sum, { startTime: breakStartTime, endTime: breakEndTime }) =>
                sum + totalTime(breakStartTime, breakEndTime),
              0
            );

            const clientApprovedBy =
              timesheet?.clientApproved?.adminId && mappedAdmins[timesheet?.clientApproved?.adminId];

            return {
              isThirdParty: !!isThirdParty,
              shiftId,
              isLocked,
              id: slotId,
              slotId,
              orderId,
              supplierAccountId,
              clientBranch: clientBranchId,
              supplierBranchId,
              supplierData: mappedSuppliers?.[supplierAccountId],
              originSupplierData: originSupplierAccountsData
                ? {
                    name: originSupplierAccountsData.name,
                    logo: originSupplierAccountsData.logo,
                    timesheetConfig: originSupplierAccountsData.timesheetConfig?.[originSupplierBranchId]
                  }
                : undefined,
              employee: {
                id: employeeSlot.employeeId,
                firstName: employeeSlot.firstName,
                lastName: employeeSlot.lastName,
                image: employeeSlot.image,
                rating: employeeSlot.employeeOverallRating
              },
              date,
              timesheetId: timesheet?.id,
              isExternal: true,
              actualIn: timesheet?.clientStartTime,
              actualOut: timesheet?.clientEndTime,
              actualCheckIn: timesheet?.checkInTime,
              actualCheckOut: timesheet?.checkOutTime,
              userActualIn: timesheet?.userStartTime,
              userActualOut: timesheet?.userEndTime,
              breaktimes: timesheet?.breaktimes,
              rating: employeeSlot.rating,
              review: employeeSlot.review,
              notes: employeeSlot.notes,
              clientApproved: timesheet?.clientApproved,
              clientApprovedBy: clientApprovedBy && `${clientApprovedBy.firstName} ${clientApprovedBy.lastName}`,
              clientApprovedAt: clientApprovedBy && timesheet?.clientApproved?.createdAt,
              clientDeclined: timesheet?.clientDeclined,
              actualInReporter: {
                type: timesheet?.startTimesheetReport?.adminId
                  ? 'admin'
                  : timesheet?.startTimesheetReport?.userId
                  ? 'location'
                  : null,
                lat: timesheet?.startTimesheetReport?.lat,
                lng: timesheet?.startTimesheetReport?.lng
              },
              actualOutReporter: {
                type: timesheet?.endTimesheetReport?.adminId
                  ? 'admin'
                  : timesheet?.endTimesheetReport?.userId
                  ? 'location'
                  : null,
                lat: timesheet?.endTimesheetReport?.lat,
                lng: timesheet?.endTimesheetReport?.lng
              },
              actualCheckInReporter: {
                type: timesheet?.checkInTimesheetReport?.adminId
                  ? 'admin'
                  : timesheet?.checkInTimesheetReport?.userId
                  ? 'location'
                  : null,
                lat: timesheet?.checkInTimesheetReport?.lat,
                lng: timesheet?.checkInTimesheetReport?.lng
              },
              actualCheckOutReporter: {
                type: timesheet?.checkOutTimesheetReport?.adminId
                  ? 'admin'
                  : timesheet?.checkOutTimesheetReport?.userId
                  ? 'location'
                  : null,
                lat: timesheet?.checkOutTimesheetReport?.lat,
                lng: timesheet?.checkOutTimesheetReport?.lng
              },
              reportMethods: keyReportMethodsByClockType(timesheet?.reportMethods),
              locationId,
              positionId,
              clientPositionId,
              clientLocationId,
              comments,
              scheduleIn: startTime,
              scheduleOut: endTime,
              actualTotalWithoutBreaks: clientTotalTime,
              actualTotal: clientTotalTime - (deductClientBreaktimesIsActive ? clientBreaksTotalTime : 0),
              scheduleTotal: totalTime(startTime, endTime),
              clientWage,
              clientWageTotal: timesheet?.id && mapPayroll ? mapPayroll?.[timesheet.id]?.value : undefined,
              payroll,
              fields: (payroll?.fields || []).reduce((prev, field) => {
                const value = (prev[field.id] || 0) + field.value;
                prev[field.id] = parseFloat(value);
                return prev;
              }, {}),
              fieldsTotal: (payroll?.fields || []).reduce((prev, field) => {
                const totalValue = (prev[field.id] || 0) + field.total;
                prev[field.id] = parseFloat(totalValue);
                return prev;
              }, {})
            };
          })
      ];
    }, [])
    .sort((a, b) => (sort.sortDirection === 'asc' ? sorter(a, b) : sorter(b, a)));

  if (formattedData.length > 0) {
    const summaryRow = {
      summarized: true,
      totalRows: formattedData?.length,
      actualTotal: formattedData.reduce((total, { actualTotal }) => total + actualTotal, 0),
      actualTotalWithoutBreaks: formattedData.reduce(
        (total, { actualTotalWithoutBreaks }) => total + actualTotalWithoutBreaks,
        0
      ),
      clientWageTotal: formattedData.reduce((total, { clientWageTotal }) => total + clientWageTotal, 0),
      breaktimes: formattedData.reduce((all, { breaktimes }) => {
        if (breaktimes) {
          all.push(...breaktimes);
        }
        return all;
      }, []),
      fields: {
        ...formattedData.reduce((output, current) => {
          (current.payroll?.fields || []).forEach((field) => {
            const value = (output[field.id] || 0) + field.value;
            output[field.id] = parseFloat(value);
            return output;
          });
          return output;
        }, {})
      },
      fieldsTotal: {
        ...formattedData.reduce((output, current) => {
          (current.payroll?.fields || []).forEach((field) => {
            const totalValue = (output[field.id] || 0) + field.total;
            output[field.id] = parseFloat(totalValue);
            return output;
          });
          return output;
        }, {})
      }
    };
    formattedData.push(summaryRow);
  }

  return formattedData;
};

export const formatTimesheetData = ({
  timeRange,
  selectedBranches,
  formatData,
  mappedClientBranches,
  mappedLabels,
  mappedLocations,
  jobs = [],
  slots = [],
  pto = [],
  leaveRecords = [],
  employees = [],
  deductBreaktimesIsActive = false,
  byClient = false,
  dateFormat = null,
  addDummy = true,
  filters = {},
  filterDataFunction = null,
  search,
  showOnlyUnapproved,
  billingStatus,
  mappedSuppliers
}) => {
  const employeesMap = getEmployeesMap(employees);

  const formattedShifts = formatShifts({
    data: slots,
    employeesMap,
    isJob: false,
    deductBreaktimesIsActive,
    byClient,
    mappedClientBranches,
    mappedLabels,
    mappedLocations,
    dateFormat,
    billingStatus,
    mappedSuppliers
  });
  const formattedJobs = formatShifts({ data: jobs, employeesMap, isJob: true, deductBreaktimesIsActive, byClient });

  const formattedDummyShifts = addDummy
    ? createDummyShifts(
        timeRange?.start,
        timeRange?.end,
        formattedJobs,
        employees,
        employeesMap,
        undefined,
        selectedBranches
      )
    : [];

  const formattedPto = formatShifts({ data: pto, employeesMap, dateFormat, byClient, isPto: true });

  const formattedLeaveRecords = formatLeaveRecords({
    data: leaveRecords,
    employeesMap,
    mappedClientBranches,
    mappedLabels,
    mappedLocations,
    dateFormat
  });

  let allData = [
    ...formattedShifts,
    ...formattedJobs,
    ...formattedDummyShifts,
    ...formattedPto,
    ...formattedLeaveRecords
  ];

  const formattedData = formatData ? formatData(allData) : allData;
  let filteredData = filterData(formattedData, search);

  if (showOnlyUnapproved) {
    filteredData = filteredData?.filter(({ approved }) => !approved);
  }

  if (filterDataFunction && filters?.basic?.length > 0) {
    const filteredValues = {};

    filters?.basic.forEach(({ slug, value }) => {
      if (!filteredValues?.[slug]) {
        filteredValues[slug] = [];
      }
      Array.isArray(value) ? filteredValues[slug].push(...value) : filteredValues[slug].push(value);
    });

    filteredData = filterDataFunction?.(filteredData, filteredValues);
  }

  return filteredData;
};

const groupByData = ({
  timeRange,
  groupByKey,
  selectedBranches,
  search,
  showOnlyUnapproved,
  nameColumnDateSort,
  formatData,
  mappedClientBranches,
  mappedLabels,
  mappedLocations,
  jobs = [],
  slots = [],
  pto = [],
  leaveRecords = [],
  employees = [],
  filters = {},
  filterDataFunction = null,
  deductBreaktimesIsActive = false,
  byClient = false,
  dateFormat = null,
  sort = {},
  sortingColumns = {},
  addDummy = true,
  billingStatus,
  mappedSuppliers
}) => {
  const filteredData = formatTimesheetData({
    timeRange,
    selectedBranches,
    formatData,
    mappedClientBranches,
    mappedLabels,
    mappedLocations,
    jobs,
    slots,
    pto,
    leaveRecords,
    employees,
    deductBreaktimesIsActive,
    byClient,
    dateFormat,
    addDummy,
    filters,
    filterDataFunction,
    search,
    showOnlyUnapproved,
    billingStatus,
    mappedSuppliers
  });

  const dataWithGroupKey = filteredData.map((item) => ({
    ...item,
    groupKey: MAP_GROUP[groupByKey](item, mappedLocations, mappedLabels, dateFormat)
  }));

  const groups = groupBy(dataWithGroupKey, 'groupKey');

  const groupLists = Object.entries(groups).map(([value, list]) => {
    const groupValue = groupByKey === TIMESHEET_GROUP_BY_OPTIONS_ENUM.PROJECT ? value.split('.')?.[0] : value;
    return {
      groupValue,
      shift: list.length,
      // hack to get the date and additional revenues from the subrows
      date:
        groupByKey === TIMESHEET_GROUP_BY_OPTIONS_ENUM.PROJECT
          ? list[0].projectStartDate
          : list.find(({ summary }) => !summary)?.date || null,
      additionalRevenues: list.find(({ summary }) => !summary)?.additionalRevenues || null,
      totalBreaktimesDuration: (list || [])
        .reduce((all, { breaktimes }) => {
          all.push(...(breaktimes || []));
          return all;
        }, [])
        .filter(
          ({ startTime, endTime }) => startTime && moment(startTime).isValid() && endTime && moment(endTime).isValid()
        )
        .map(({ startTime, endTime }) => totalTime(startTime, endTime))
        .reduce((all, duration) => all + duration || 0, 0),
      fields: {
        ...list.reduce((output, current) => {
          (current.payrollWithLabourCost?.fields || current.payroll?.fields || []).forEach((field) => {
            output[field.id] = (output[field.id] || 0) + field.value;
          });
          return output;
        }, {})
      },
      accountFields: {
        ...list.reduce((output, current) => {
          (current.employee?.fields || []).forEach((field) => {
            output[field.id] = field.value;
          });
          return output;
        }, {})
      },
      fieldsTotal: {
        ...list.reduce((output, current) => {
          (current.payrollWithLabourCost?.fields || current.payroll?.fields || []).forEach((field) => {
            output[field.id] = (output[field.id] || 0) + field.total;
          });
          return output;
        }, {})
      },
      actualTotal: list.reduce((sum, { actualTotal }) => sum + actualTotal, 0),
      scheduleTotal: list.reduce((sum, { scheduleTotal }) => sum + scheduleTotal, 0),
      clientWageTotal: list.reduce((sum, { clientWageTotal }) => sum + clientWageTotal, 0),
      difference: list.reduce((sum, { difference }) => sum + difference, 0),

      // HACK: we are putting groupValue inside
      subRows: list.map((item) => ({ ...item, groupValue })),
      // group is selectable if exists one item that can be selected
      selectable: list.some((i) => i.selectable),
      payrollTotal: list.reduce((sum, { payroll }) => sum + (payroll?.value || 0), 0),
      labourCostTotal: list.reduce((sum, { payrollWithLabourCost }) => sum + (payrollWithLabourCost?.value || 0), 0),
      payrollTotalWithVat: list.reduce((sum, { payrollTotalWithVat }) => sum + (payrollTotalWithVat || 0), 0),
      isMultiDay: isProjectMultiDay({ date: list[0].projectStartDate, endDate: list[0].projectEndDate })
    };
  });

  return sortedGroupedData({
    sortingColumns,
    nameColumnDateSort,
    groupLists,
    groupByKey,
    sort
  });
};

export default groupByData;

export const generateCSV = ({
  columns,
  t,
  timeRange,
  from,
  to,
  data,
  search,
  mappedPositions,
  mappedFieldsValue,
  mappedJobTypes,
  accountFields,
  mappedClientBranches,
  mappedLabels,
  mappedLocations,
  filters = {},
  timeFormat = 'HH:mm',
  dateFormat = 'DD/MM/YYYY',
  deductBreaktimesIsActive = false,
  accountName,
  isShowLastNameFirst = false,
  timesheetFields = []
}) => {
  const groupedData = groupByData({
    timeRange,
    filters,
    jobs: data?.jobs,
    slots: data?.slots,
    leaveRecords: data?.leave,
    search,
    employees: data?.employees,
    groupByKey: 'date',
    deductBreaktimesIsActive,
    filterDataFunction: applyFiltersOnTimesheetData
  });

  const employeesMap = getEmployeesMap(data?.employees) || {};

  const getEmployeeFields = (row) => {
    if (!row?.employee?.id) {
      return [];
    }

    const srcFields = row.isExternal ? row.employee.fields || [] : employeesMap[row?.employee?.id]?.fields || [];

    return reduceEmployeeFields(accountFields, mappedFieldsValue, srcFields);
  };

  const visibleData = groupedData.reduce((prev, { subRows }) => {
    const sortedSubRows = subRows.sort(SORT_BY_DATE('shiftDate'));
    return [
      ...prev,
      ...sortedSubRows.map((row) => {
        let formattedBreaktimes = '';

        const employeeFields = getEmployeeFields(row);

        if (row?.breaktimes && row.breaktimes.length > 0) {
          formattedBreaktimes = row.breaktimes
            .map(({ startTime, endTime }) => {
              const formattedStartTime = moment(startTime).format(timeFormat);
              const formattedEndTime = endTime && moment(endTime).isValid() ? moment(endTime).format(timeFormat) : null;
              return `${formattedStartTime} - ${formattedEndTime || t('onBreak')}`;
            })
            .join(',');
        }
        const identification = row.isExternal ? '' : row.employee?.identification ?? '';
        const employeeID = isNaN(identification) ? identification : Number(identification);

        const clientBranch = row.clientBranchId && mappedClientBranches[row.clientBranchId];
        const admin = clientBranch?.admins?.find((branchAdmin) => branchAdmin.id === row.clientApproved?.adminId);

        return conditionalColumns(
          {
            date: moment(row.isJob || row.isLeave ? row.date : row.shiftDate, API_DATE_TIME_FORMAT).format(dateFormat),
            firstName: row.employee.firstName,
            lastName: row.employee.lastName,
            fullName: getEmployeeDisplayName(row.employee.firstName, row.employee.lastName, isShowLastNameFirst),
            employeeId: employeeID || '',
            email: row.employee.email,
            external: row.isExternal ? t('external') : '',
            supplier: row.isExternal && row.supplierData ? row.supplierData.name : '',
            client: row.clientBranchId ? mappedClientBranches[row.clientBranchId]?.name : '',
            costCenter: row.costCenter?.identifier || row.costCenter?.name || '',
            position:
              row.isExternal && row.supplierData?.positionSlug
                ? t(row.supplierData.positionSlug)
                : mappedPositions[row.positionId]?.label ?? '',
            eventName: row.name || '',
            shift: generateShiftName(row, mappedJobTypes, mappedLabels, mappedLocations),
            areaName: row.locationId ? mappedLocations[row.locationId]?.name || '' : '',
            shiftStart: row.scheduleIn ? moment(row.scheduleIn).format(timeFormat) : '',
            in: row.actualIn ? moment(row.actualIn).format(timeFormat) : '',
            checkedIn: row.actualCheckIn ? moment(row.actualCheckIn).format(timeFormat) : '',
            shiftEnd: row.scheduleOut ? moment(row.scheduleOut).format(timeFormat) : '',
            out: row.actualOut ? moment(row.actualOut).format(timeFormat) : '',
            checkedOut: row.actualCheckOut ? moment(row.actualCheckOut).format(timeFormat) : '',
            reviewOnEmployee: row.review || '',
            total: row.actualIn && row.actualOut ? getDurationFromMilliseconds(row.actualTotal) : '',
            breaks: formattedBreaktimes,
            approved: row.approved || row?.timesheet?.approved ? t('yes') : t('no'),
            rating: row.rating || '',
            clientApproved:
              row.clientApproved || row?.timesheet?.clientApproved
                ? `${admin?.firstName || ''} ${admin?.lastName || ''}`
                : '',
            ...employeeFields,
            ...getTimesheetFieldsForExport({ row, timesheetFields, t, dateFormat })
          },
          columns
        );
      })
    ];
  }, []);

  exportExcel({ baseData: visibleData || [], name: [accountName, t('timesheet'), from, to].join(' - ') });
};

export const generateOrderCSV = ({
  t,
  date,
  columns,
  dateFormat,
  timeFormat,
  data,
  mappedSuppliers,
  mappedSupplierPositions
}) => {
  const visibleData = data
    .filter((row) => !row?.summarized)
    .map((row) => {
      let formattedBreaktimes = '';

      if (row?.breaktimes && row.breaktimes.length > 0) {
        formattedBreaktimes = row.breaktimes
          .map(({ startTime, endTime }) => {
            const formattedStartTime = moment(startTime).format(timeFormat);
            const formattedEndTime = endTime && moment(endTime).isValid() ? moment(endTime).format(timeFormat) : null;
            return `${formattedStartTime} - ${formattedEndTime || t('onBreak')}`;
          })
          .join(',');
      }

      const shiftTime =
        row.scheduleOut && moment(row.scheduleOut).isValid()
          ? `${moment(row.scheduleIn).format(timeFormat)} - ${moment(row.scheduleOut).format(timeFormat)}`
          : `${moment(row.scheduleIn).format(timeFormat)} `;

      return conditionalColumns(
        {
          date: moment(row.date).format(dateFormat),
          approved: row.clientApproved ? t('yes') : '',
          clientApprovedBy: row.clientApprovedBy || '',
          clientApprovedAt: row.clientApprovedAt
            ? moment(row.clientApprovedAt).format(`${dateFormat} ${timeFormat}`)
            : '',
          firstName: row.employee?.firstName,
          lastName: row.employee?.lastName,
          position: row.positionId ? mappedSupplierPositions[row.positionId]?.label : '',
          supplierName: row.supplierAccountId ? mappedSuppliers[row.supplierAccountId]?.name : '',
          shift: shiftTime,
          in: row.actualIn ? moment(row.actualIn).format(timeFormat) : '',
          out: row.actualOut ? moment(row.actualOut).format(timeFormat) : '',
          breaks: formattedBreaktimes,
          totalTime: row.actualIn && row.actualOut ? getDurationFromMilliseconds(row.actualTotal) : '',
          clientWage: row.clientWage?.hourlyWage || '',
          clientWageTotal: row.clientWageTotal || '',
          reviewOnEmployee: row.review || ''
        },
        columns
      );
    });

  exportExcel({ baseData: visibleData || [], name: `${t('orders')} ${date} ` });
};

export const isUnapprovedWithoutErrorsRow = ({ rowData, endTimeTolerance = 0 }) => {
  const { isJob: isDaily, scheduleIn, scheduleOut, actualIn, actualOut, clientActualIn, clientActualOut } = rowData;

  if (isDaily) {
    return true;
  }

  const enteredBeforeStartTime = moment(actualIn).isBefore(moment(scheduleIn));

  //END TIME
  const allowedClockInTimeWithTolerance = moment(scheduleOut).add(endTimeTolerance, 'minutes');
  const allowedClockInTimeWithoutTolerance = moment(scheduleOut);

  const exitedAfterEndTimeWithTolerance = moment(actualOut).isAfter(moment(allowedClockInTimeWithTolerance));
  const exitedAfterEndTimeWithoutTolerance = moment(actualOut).isAfter(moment(allowedClockInTimeWithoutTolerance));

  const enterTimeInconsistency = !!(clientActualIn && !moment(actualIn).isSame(moment(clientActualIn)));
  const exitTimeInconsistency = !!(clientActualOut && !moment(actualOut).isSame(moment(clientActualOut)));

  const hasTimeIssues =
    enteredBeforeStartTime || enterTimeInconsistency || exitTimeInconsistency || exitedAfterEndTimeWithTolerance;

  return {
    hasIssuesWithoutTolerance: hasTimeIssues || exitedAfterEndTimeWithoutTolerance,
    hasIssuesWithTolerance: hasTimeIssues
  };
};

export const isReadOnly = (data, readOnlyInput = false) => {
  return (
    readOnlyInput ||
    !!data?.isLocked ||
    !!data?.isPto ||
    !!data?.isLeave ||
    data?.payrollProcess?.status === PAYROLL_PROCESS_STATUS.PROCESSED
  );
};

export const isPayrollFieldEditableOnTimesheet = (data) =>
  !!data?.timesheetStartTime && !!data?.timesheetEndTime && !isReadOnly(data);

export const isCellEditable = ({ readOnly, updateFunc, isExternal, allowUpdateExternal }) => {
  if (readOnly) return false;
  if (!updateFunc) return false;
  if (isExternal && !allowUpdateExternal) return false;
  return true;
};

const END_TIME_TOLERANCE_MIN = 30;

export const isCellClearable = ({ isClientApproved, isApproved, allowClear, isUnlockPayrollPrivilege }) =>
  isClientApproved || isApproved ? allowClear && isUnlockPayrollPrivilege : allowClear;

export const generateRowsToApprove = ({ unapprovedRows, approveAllWithoutDiscrepanciesAddTolerance }) => {
  const { unapprovedWithoutErrorsRowsWithTolerance = [], unapprovedWithoutErrorsRowsWithoutTolerance = [] } = (
    unapprovedRows || []
  ).reduce((output, item) => {
    const res = isUnapprovedWithoutErrorsRow({
      rowData: item,
      endTimeTolerance: END_TIME_TOLERANCE_MIN
    });

    output.unapprovedWithoutErrorsRowsWithTolerance ??= [];
    output.unapprovedWithoutErrorsRowsWithoutTolerance ??= [];

    // no issues - allow to approve in both cases
    if (!res.hasIssuesWithTolerance && !res.hasIssuesWithoutTolerance) {
      output.unapprovedWithoutErrorsRowsWithTolerance.push(item);
      output.unapprovedWithoutErrorsRowsWithoutTolerance.push(item);
      return output;
    }

    // if no issues with tolerance
    if (!res.hasIssuesWithTolerance) {
      output.unapprovedWithoutErrorsRowsWithTolerance.push(item);
      return output;
    }

    if (!res.hasIssuesWithoutTolerance) {
      output.unapprovedWithoutErrorsRowsWithoutTolerance.push(item);
      return output;
    }

    return output;
  }, {});

  const unapprovedWithoutErrorsRowsInner = approveAllWithoutDiscrepanciesAddTolerance
    ? unapprovedWithoutErrorsRowsWithTolerance
    : unapprovedWithoutErrorsRowsWithoutTolerance;

  return {
    unapprovedWithoutErrorsRows: unapprovedWithoutErrorsRowsInner,
    totalToApprove: unapprovedWithoutErrorsRowsWithTolerance.length
  };
};
